'use client';

import { useEffect, useState } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import NewsFilters from './components/NewsFilters';
import NewsGrid from './components/NewsGrid';
import TrendingSidebar from './components/TrendingSidebar';
import AdSense from '@/components/adsense/AdSense';
import { financialDataService, type NewsArticle } from '@/lib/services/financialDataService';

export default function News() {
  const [activeCategory, setActiveCategory] = useState('cat_all');
  const [searchQuery, setSearchQuery] = useState('');
  const [allArticles, setAllArticles] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const data = await financialDataService.getNewsArticles(50);
        setAllArticles(data);
        setError(null);
      } catch (err: any) {
        setError(err?.message || 'Failed to load news');
      } finally {
        setLoading(false);
      }
    }
    fetchData();

    // Subscribe to real-time updates
    const unsubscribe = financialDataService.subscribeToNewsArticles((updatedData) => {
      setAllArticles(updatedData);
    }, 50);

    return () => {
      unsubscribe();
    };
  }, []);

  const filteredArticles = allArticles?.filter((article) => {
    const matchesCategory =
      activeCategory === 'cat_all' ||
      article.category.toLowerCase() === activeCategory.replace('cat_', '');
    const matchesSearch =
      searchQuery === '' ||
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  }) || [];

  return (
    <>
      <Header />
      <main className="min-h-screen pt-32 pb-16">
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Page Header */}
          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4 font-display">
              Financial News & Analysis
            </h1>
            <p className="text-xl text-muted-foreground">
              Curated from 50+ trusted sources, updated every 15 minutes
            </p>
          </div>

          {/* AdSense Ad */}
          <div className="mb-8">
            <AdSense
              adClient={process.env.NEXT_PUBLIC_ADSENSE_ID || ''}
              adSlot="5544332211"
              adFormat="auto"
              adStyle={{ display: 'block', minHeight: '250px' }}
            />
          </div>

          {/* Filters */}
          <NewsFilters
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* News Grid */}
            <div className="lg:col-span-2">
              <div className="mb-6 flex items-center justify-between">
                <p className="text-muted-foreground">
                  {loading ? 'Loading...' : `${filteredArticles.length} articles found`}
                </p>
              </div>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-muted-foreground">Loading news articles...</div>
                </div>
              ) : error ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-secondary">Error: {error}</div>
                </div>
              ) : (
                <NewsGrid articles={filteredArticles} />
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-32">
                <TrendingSidebar />
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}